import { css, Heading, Label, Paragraph, Pill, Stack, styled } from 'canoe';
import { FieldMetaProps } from 'formik';
import React from 'react';

import HotspotGroupType from '!!react-svg-loader!../assets/HotspotGroup.svg';
import InlineType from '!!react-svg-loader!../assets/InlineType.svg';
import ModalType from '!!react-svg-loader!../assets/ModalType.svg';
import TourType from '!!react-svg-loader!../assets/TourType.svg';
import { ContentType } from '@candu-ai/content/types';
import { Flags, useFlags } from 'commons/hooks';

const data = [
  {
    value: ContentType.Inline,
    label: 'Inline',
    description: 'Embed content into any page',
    svg: InlineType,
  },
  {
    value: ContentType.Modal,
    label: 'Overlay',
    description: 'Create Modals, Pop-ups and Slide-outs',
    svg: ModalType,
  },
  {
    value: ContentType.HotspotGroup,
    label: 'Hotspots',
    description: 'Add a tooltip & beacon to an existing element',
    svg: HotspotGroupType,
  },
  {
    value: ContentType.Tour,
    label: 'Tour',
    description: 'Guide users through multiple steps',
    svg: TourType,
    isBeta: true,
  },
  {
    value: ContentType.Banner,
    label: 'Banner',
    description: 'Add Banner to your existing product',
    svg: HotspotGroupType,
  },
];

const ColumnContainer = styled.div<any>`
  ${({ theme }) => css`
    display: grid;
    grid-column-gap: ${theme.spaces.xs};
    grid-template-columns: ${(props: any) => `repeat(${props.columns}, 1fr)`};
  `}
`;
const StyledButton = styled.button<{ isSelected?: boolean }>`
  ${({ theme, isSelected, disabled }) => css`
    outline: none;
    width: 256px;
    background: transparent;
    border: 1px solid ${theme.colors.GlitterGray};
    cursor: pointer;
    margin: auto;
    white-space: nowrap;
    padding: ${theme.spaces.s};
    border-radius: ${theme.borderRadius.small};
    border: 1px solid ${theme.colors.GlitterGray};
    transition: ${theme.transitions.default()};

    &:hover {
      border-color: ${theme.colors.borderFocus};
      box-shadow: ${theme.boxShadow.outline(theme.colors.borderFocus)};
    }

    &:active,
    &:focus,
    &:focus-within {
      outline: none;
      border-color: ${theme.colors.AquaTeal};
      box-shadow: ${theme.boxShadow.outline(theme.colors.AquaTeal)};
    }

    ${disabled &&
    css`
      filter: grayscale(1);
      cursor: not-allowed;

      &:hover {
        border-color: ${theme.colors.GlitterGray};
        box-shadow: none;
      }
    `}
    ${isSelected &&
    css`
      border: 1px solid ${theme.colors.AquaTeal};
      box-shadow: ${theme.boxShadow.outline(theme.colors.AquaTeal)};
    `}
    p {
      font-size: ${theme.fonts.small.fontSize};
    }
  `}
`;

const Flex = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.spaces.xs};
  `}
`;

const CardDescription = styled(Paragraph)`
  white-space: normal;
  min-height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface ContentTypeOptionsProps extends FieldMetaProps<string> {
  selectedType?: ContentType;
  modalOnly?: boolean;
  noHotspots?: boolean;
  onSelect: (arg: ContentType) => void;
}

export const ContentTypeOptions = React.memo(
  ({ selectedType, onSelect, modalOnly, noHotspots, ...props }: ContentTypeOptionsProps) => {
    const { [Flags.HOTSPOTS]: withHotspots } = useFlags();
    const { [Flags.HOTSPOTS_UPSELL]: withHotspotsUpsell } = useFlags();
    const { [Flags.TOURS]: withTours } = useFlags();
    const { [Flags.BANNERS]: withBanners } = useFlags();

    let columns = 2;
    if ((withHotspots || withHotspotsUpsell) && !noHotspots) {
      columns += 1;
    }
    if (withTours) {
      columns += 1;
    }

    return (
      <Stack justify="center" space="s">
        <Heading size="XS" element="h3" m="none">
          What type of Content do you want to create?
        </Heading>
        <ColumnContainer columns={columns}>
          {data.map((contentType) => {
            if (contentType.value === ContentType.Tour && !withTours) {
              return null;
            }
            if (contentType.value === ContentType.Banner && !withBanners) {
              return null;
            }

            if (
              contentType.value === ContentType.HotspotGroup &&
              !withHotspots &&
              withHotspotsUpsell &&
              !noHotspots
            ) {
              return (
                <StyledButton
                  {...props}
                  type="button"
                  key={contentType.value}
                  id={contentType.value}
                  isSelected={contentType.value === selectedType}
                  // @ts-ignore
                  onClick={() => Candu?.triggerModal({ slug: 'hotspots-upsell' })}
                >
                  <Stack space="xs">
                    <contentType.svg />
                    <Label>✨ NEW ✨ {contentType.label} 🔒</Label>
                    <CardDescription size="short">
                      Upgrade for {contentType.label} today
                    </CardDescription>
                  </Stack>
                </StyledButton>
              );
            }

            if (contentType.value === ContentType.HotspotGroup && (!withHotspots || noHotspots)) {
              return null;
            }
            return (
              <StyledButton
                {...props}
                type="button"
                key={contentType.value}
                id={contentType.value}
                isSelected={contentType.value === selectedType}
                onClick={() => onSelect(contentType.value)}
                disabled={contentType.value === ContentType.Inline && modalOnly}
              >
                <Stack space="xs">
                  <contentType.svg />
                  <Flex>
                    <Label>{contentType.label}</Label>
                    {contentType.isBeta && (
                      <Pill size="slim" rounded={false} variant="secondary" appearance="outline">
                        Beta
                      </Pill>
                    )}
                  </Flex>
                  <CardDescription size="short">{contentType.description}</CardDescription>
                </Stack>
              </StyledButton>
            );
          })}
        </ColumnContainer>
      </Stack>
    );
  },
);
